// libs
import React, { memo, forwardRef, useEffect } from 'react';

// styles
import {
  Label,
  Main,
  Bar,
  HelperText,
  FlexWrapper,
  Sibling,
  InputWrapper,
  InputElement,
  IconWrapper,
} from './styles';

// def
import { IInputProps, ReqElement } from './types';

// hooks
import useTheme from '@nykaa/ui-components/themes/hooks/useTheme';
import useHelper from './helpers';

// types
import { Theme } from '@nykaa/ui-components/themes/types';

// constants
import { SPACING_NAMES } from './constants';

// icons
import { useStoreIcon, IconName } from 'shared/icons';

const ALERT_ICON_SIZE = '16px';
const VERIFIED_ICON_SIZE = '16px';

function BaseInput(
  {
    className = '',
    inputClassName = '',
    label,
    placeholder = '',
    type = 'text',
    value = '',
    onChange,
    isError = false,
    disabled = false,
    helperText = '',
    autoFocus = false,
    onFocus,
    trailingComponent = null,
    ariaLabel,
    ariaDescribedby,

    ...restProps
  }: IInputProps,
  ref: React.RefObject<ReqElement>
) {
  const hasTrailingComponent = trailingComponent !== null;
  const spacingType = SPACING_NAMES.DEFAULT;
  const theme: Theme = useTheme();
  const AlertNegativeFilledIcon = useStoreIcon(IconName.ALERT_NEGATIVE_FILLED);
  const AlertPositiveFilledIcon = useStoreIcon(IconName.ALERT_POSITIVE_FILLED);

  const { barClassName, inFocus, isValid, setFocus, setOutOfFocus } = useHelper({
    autoFocus,
    value,
    onFocus,
    isError,
  });

  useEffect(() => {
    if (autoFocus) {
      setFocus();
    }
  }, [autoFocus, setFocus]);

  return (
    <section className={`inputContainer ${className}`} data-test="mainBaseInput">
      <Main
        inFocus={inFocus}
        isDisabled={disabled}
        isValid={isValid}
        isError={isError}
        spacingType={spacingType}
      >
        <FlexWrapper hasTrailingComponent={hasTrailingComponent}>
          <InputWrapper className="inputWrapper">
            <InputElement
              aria-invalid={isError}
              aria-describedby={ariaDescribedby || helperText}
              aria-label={ariaLabel || label}
              inFocus={inFocus}
              type={type}
              value={value}
              onChange={onChange}
              placeholder={placeholder}
              className={`input ${inputClassName}`}
              onFocus={setFocus}
              onBlur={setOutOfFocus}
              data-test="inputElement"
              autoFocus={autoFocus}
              ref={ref}
              {...restProps}
              data-at="pincode-input"
              data-cs-mask
            />
            <Label className="label" inFocus={inFocus} isValid={isValid}>
              {label}
            </Label>
          </InputWrapper>
          {hasTrailingComponent && (
            <Sibling className="trailingComponent">{trailingComponent}</Sibling>
          )}
        </FlexWrapper>

        <Bar className={barClassName} isError={isError} inFocus={inFocus} isValid={isValid} />
      </Main>
      {helperText !== '' && (
        <HelperText className="helperText" isError={isError}>
          <span data-at={isError ? 'pincode-err' : 'delivery-city'}>{helperText}</span>
          {isError ? (
            <IconWrapper>
              <AlertNegativeFilledIcon
                color={theme?.colors?.negativePrimary}
                size={ALERT_ICON_SIZE}
              />
            </IconWrapper>
          ) : (
            <IconWrapper>
              <AlertPositiveFilledIcon
                color={theme?.colors?.textSecondary}
                size={VERIFIED_ICON_SIZE}
              />
            </IconWrapper>
          )}
        </HelperText>
      )}
    </section>
  );
}

export default memo(forwardRef(BaseInput));
